/**
 * ============================================================================
 * legacySupport.js - Rilevamento funzionalita' per le ricadute sui browser
 *                    vecchi
 * ============================================================================
 *
 * A COSA SERVE
 * Alcune regole CSS del sito usano funzionalita' che Safari ha aggiunto tardi
 * (il `gap` dentro flexbox arriva con iOS 14.5, `:focus-visible` e `:has()`
 * con iOS 15.4). Sui browser che non le hanno, quelle dichiarazioni vengono
 * ignorate e il layout si rompe.
 *
 * Questo file mette una classe sull'<html> per ogni funzionalita' MANCANTE,
 * cosi' il CSS puo' offrire una ricaduta mirata:
 *
 *     .no-flexgap .menu > * + * { margin-left: 2rem; }
 *
 * IL PRESUPPOSTO CHE GUIDA TUTTO
 * I dispositivi vecchi sono una minoranza; i moderni sono tutto il resto.
 * Quindi il rischio accettabile verso i moderni e' ZERO, non "basso".
 *
 * Da qui discendono due scelte non negoziabili:
 *
 * 1. SUI BROWSER MODERNI NON VIENE AGGIUNTA NESSUNA CLASSE.
 *    L'elemento <html> resta identico a com'e' oggi, quindi nessuna regola di
 *    ricaduta puo' agganciarsi a niente. E' cio' che rende le modifiche
 *    dimostrabilmente invisibili (vedi tools/compat/css_gate_diff.py).
 *
 * 2. NEL DUBBIO SI ASSUME "MODERNO".
 *    Se un rilevamento fallisce o lancia un errore, si assume che la
 *    funzionalita' CI SIA e non si aggiunge la classe. Sbagliare in questa
 *    direzione significa lasciare un dispositivo vecchio senza ricaduta:
 *    fastidioso ma innocuo. Sbagliare nell'altra significa attivare una
 *    ricaduta su un browser moderno, cioe' rompere il sito per la
 *    maggioranza. Le due direzioni non sono simmetriche.
 *
 * SI RILEVANO FUNZIONALITA', NON VERSIONI
 * Non si guarda lo user agent. Se un domani un polyfill fornisce la
 * funzionalita', il rilevamento se ne accorge da solo e la ricaduta non si
 * attiva. E' anche l'unico modo di essere corretti sui browser che non
 * conosciamo.
 *
 * MODALITA' DI PROVA
 * Con `?legacy=1` nell'indirizzo si forzano TUTTE le classi anche su un
 * browser moderno. Serve a controllare che le ricadute siano scritte bene
 * senza avere un dispositivo vecchio sottomano. Non si attiva mai da sola.
 *
 * INTERRUTTORE GENERALE
 * Tutte le ricadute a classe dipendono da questo unico file: togliere la
 * chiamata a `detectLegacySupport()` da main.js le disattiva tutte insieme,
 * senza toccare il CSS e senza revert.
 * ============================================================================
 */

/**
 * Il `gap` dentro flexbox esiste da iOS 14.5 / Safari 14.1.
 * Non e' rilevabile con `@supports (gap: 1rem)`: quella condizione e' VERA
 * anche su Safari 13, perche' li' il gap funziona in grid. L'unico modo
 * affidabile e' misurare.
 *
 * Si costruisce un contenitore flex in colonna con due figli alti zero e un
 * gap noto. Se il gap viene applicato, l'altezza del contenitore e' pari al
 * gap; altrimenti e' zero.
 *
 * @returns {boolean} true se il gap in flexbox funziona
 */
function supportsFlexGap() {
  try {
    const GAP = 10

    const probe = document.createElement('div')
    probe.style.display = 'flex'
    probe.style.flexDirection = 'column'
    probe.style.rowGap = GAP + 'px'
    probe.style.position = 'absolute'
    probe.style.visibility = 'hidden'
    // ATTENZIONE: NON si imposta l'altezza. Fissarla a 0 azzererebbe anche
    // scrollHeight, e la misura direbbe sempre "gap assente" anche su un
    // browser che lo supporta - cioe' attiverebbe la ricaduta proprio sulla
    // maggioranza degli utenti. Errore gia' commesso e colto dal banco di
    // prova: l'altezza deve restare libera di adattarsi al contenuto.
    probe.appendChild(document.createElement('div'))
    probe.appendChild(document.createElement('div'))

    document.body.appendChild(probe)
    // Due figli alti zero: con il gap applicato il contenitore e' alto GAP,
    // senza e' alto 0. Si usa un valore ampio invece di 1px cosi' nessun
    // arrotondamento sub-pixel puo' confondere le due risposte.
    const applied = probe.scrollHeight >= GAP
    document.body.removeChild(probe)

    return applied
  } catch (e) {
    // Nel dubbio: moderno. Vedi la nota 2 in cima al file.
    return true
  }
}

/**
 * Verifica il supporto di un selettore tramite `CSS.supports('selector(...)')`.
 *
 * Su un browser che non conosce nemmeno la forma `selector()`, la chiamata
 * restituisce false: e' la risposta giusta, perche' un browser cosi' vecchio
 * non conosce neanche il selettore che stiamo chiedendo.
 *
 * @param {string} sel selettore da verificare, es. ':focus-visible'
 * @returns {boolean} true se il selettore e' supportato
 */
function supportsSelector(sel) {
  try {
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
      // Browser molto vecchio. Si assume comunque "moderno" per non
      // rischiare di attivare ricadute dove non servono.
      return true
    }
    return CSS.supports(`selector(${sel})`)
  } catch (e) {
    return true
  }
}

/**
 * `aspect-ratio` esiste da iOS 15.
 *
 * Sarebbe rilevabile anche con `@supports not (aspect-ratio: 1/1)` senza
 * scomodare il JavaScript. Si e' scelto ugualmente il cancello a classe per
 * un motivo pratico: `@supports not` non e' forzabile su un browser moderno,
 * quindi la ricaduta non sarebbe ISPEZIONABILE con ?legacy=1. Potendo
 * scegliere fra una soluzione piu' elegante e una verificabile, sotto questo
 * presupposto vince la verificabile.
 *
 * @returns {boolean} true se aspect-ratio e' supportato
 */
function supportsAspectRatio() {
  try {
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
      return true
    }
    return CSS.supports('aspect-ratio', '1 / 1')
  } catch (e) {
    return true
  }
}

/**
 * `-webkit-line-clamp` tronca davvero, quando dentro ci sono piu' paragrafi?
 *
 * La proprieta' esiste da Safari 5, quindi chiedere "e' supportata" non serve a
 * niente: la risposta e' sempre si'. Il problema e' un altro, ed e' un difetto
 * di rendering dei WebKit vecchi: con `display: -webkit-box` e piu' figli di
 * tipo blocco - com'e' il testo che arriva dal CMS, fatto di tanti <p> - le
 * righe si accavallano invece di essere tagliate. Il risultato e' testo
 * sovrapposto e illeggibile, visto sul Mac di collaudo.
 *
 * Quindi non si controlla la presenza della proprieta', si MISURA il
 * comportamento: si costruisce un contenitore con tre paragrafi, lo si tronca a
 * due righe da 20px, e si guarda quanto e' alto. Se il troncamento funziona
 * l'altezza sta attorno ai 40px; se non funziona e' molto di piu'.
 *
 * @returns {boolean} true se il troncamento a righe funziona
 */
function supportsLineClamp() {
  try {
    const probe = document.createElement('div')
    probe.style.position = 'absolute'
    probe.style.visibility = 'hidden'
    probe.style.width = '200px'
    probe.style.fontSize = '16px'
    probe.style.lineHeight = '20px'
    probe.style.display = '-webkit-box'
    probe.style.webkitLineClamp = '2'
    probe.style.webkitBoxOrient = 'vertical'
    probe.style.overflow = 'hidden'

    for (let i = 0; i < 3; i++) {
      const par = document.createElement('p')
      par.style.margin = '0'
      par.textContent = 'testo di prova lungo abbastanza da occupare piu di una riga dentro il contenitore di misura'
      probe.appendChild(par)
    }

    document.body.appendChild(probe)
    const height = probe.getBoundingClientRect().height
    document.body.removeChild(probe)

    // due righe da 20px fanno 40: si lascia margine per gli arrotondamenti
    return height > 0 && height <= 60
  } catch (e) {
    // Nel dubbio: moderno.
    return true
  }
}

/**
 * Vero se l'indirizzo contiene `?legacy=1`, cioe' se stiamo controllando a
 * mano le ricadute su un browser moderno.
 *
 * @returns {boolean}
 */
export function legacyForced() {
  try {
    return window.location.search.indexOf('legacy=1') !== -1
  } catch (e) {
    return false
  }
}

/**
 * Esegue i rilevamenti e mette le classi sull'<html>.
 *
 * Va chiamata PRIMA del mount di Vue, cosi' il primo disegno ha gia' le
 * classi giuste e non si vede un salto di layout.
 *
 * Richiede che `document.body` esista: gli script del bundle sono `defer`,
 * quindi girano a documento gia' analizzato. Se per qualche motivo il body
 * non c'e', `supportsFlexGap` va in catch e restituisce true (nessuna
 * classe), che e' la direzione sicura.
 */
export function detectLegacySupport() {
  const root = document.documentElement
  const forced = legacyForced()

  const checks = [
    ['no-flexgap', supportsFlexGap()],
    ['no-focusvisible', supportsSelector(':focus-visible')],
    // NIENTE 'no-has': la ricaduta per :has() non e' esprimibile in CSS,
    // perche' richiede di selezionare il genitore in base ai figli.
    // Servirebbe aggiungere una classe nei template, cioe' toccare codice
    // che gia' funziona. Finche' nessuna regola puo' usarla, la classe non
    // si mette: meglio niente che una classe che promette una ricaduta
    // inesistente.
    // Effetto oggi su iOS < 15.4: due regole che nascondono contenitori
    // vuoti (NewsSinglePage, FilialiSubPage) non si applicano.
    ['no-aspectratio', supportsAspectRatio()],
    ['no-lineclamp', supportsLineClamp()]
  ]

  checks.forEach(([className, supported]) => {
    if (forced || !supported) {
      root.classList.add(className)
    }
  })

  // Marca la simulazione come tale. Serve al CSS per neutralizzare le
  // proprieta' che il browser moderno supporta comunque (il `gap` in flexbox,
  // `aspect-ratio`): senza, si vedrebbero gap E ricaduta sommati, cioe' una
  // simulazione che mente. Su un dispositivo vecchio vero questa classe non
  // c'e' mai.
  if (forced) {
    root.classList.add('legacy-sim')
  }
}


/**
 * Registra un ascoltatore sul cambio di una media query.
 *
 * `MediaQueryList` e' diventato un `EventTarget` (quindi con
 * `addEventListener`) solo da Safari 14 / iOS 14. Prima esisteva unicamente
 * `addListener`, che oggi e' deprecato ma continua a funzionare ovunque.
 *
 * Sul browser moderno viene eseguito il primo ramo, che fa esattamente quello
 * che il codice faceva prima di questa funzione: il comportamento e'
 * identico, non solo equivalente.
 *
 * @param {MediaQueryList} mql
 * @param {Function} handler
 */
export function onMediaChange(mql, handler) {
  if (mql && typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler)
    return
  }
  if (mql && typeof mql.addListener === 'function') {
    mql.addListener(handler)
  }
}

/**
 * Rimuove l'ascoltatore registrato da `onMediaChange`, con la stessa logica.
 * Va tenuta allineata a quella: registrare col nuovo e togliere col vecchio
 * lascerebbe un ascoltatore appeso.
 *
 * @param {MediaQueryList} mql
 * @param {Function} handler
 */
export function offMediaChange(mql, handler) {
  if (mql && typeof mql.removeEventListener === 'function') {
    mql.removeEventListener('change', handler)
    return
  }
  if (mql && typeof mql.removeListener === 'function') {
    mql.removeListener(handler)
  }
}

/**
 * Crea un ResizeObserver se il browser ce l'ha, altrimenti restituisce null.
 *
 * `ResizeObserver` arriva con iOS 13.4: sotto quella versione `new
 * ResizeObserver(...)` lancia un ReferenceError che, se non protetto, si
 * porta via il montaggio del componente che lo chiama.
 *
 * Chi la usa deve controllare il null prima di `observe` e `disconnect`. La
 * perdita e' accettabile e limitata: senza l'observer non si ricalcola quando
 * un elemento cambia dimensione da solo, ma i ricalcoli legati a `resize`,
 * `scroll` e `load` restano tutti al loro posto.
 *
 * Sul browser moderno restituisce `new ResizeObserver(callback)`, cioe'
 * esattamente l'oggetto che il codice creava prima.
 *
 * @param {Function} callback
 * @returns {ResizeObserver|null}
 */
export function createResizeObserver(callback) {
  try {
    if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
      return null
    }
    return new window.ResizeObserver(callback)
  } catch (e) {
    return null
  }
}
